<template>
    <PageHeader :layout="pageMeta?.layout"/>

    <PodcastPlayer v-if="globalPlayerState" :tracks="mediaStore.tracks" />

    <SlideLeftTransition>
        <PageNotification v-if="notificationState" :content="notificationContent" />
    </SlideLeftTransition>

    <NuxtLayout :name="layout">
        <SlideMenuDownTransition>
            <MegaMenu v-if="mState" />
        </SlideMenuDownTransition>
        
        <NuxtPage 
            :page-key="(route) => route.fullPath"
            :class="{
                'geeks-page': pageMeta?.layout === 'layout-2',
                'foodclash-page': pageMeta?.layout === 'layout-3'
            }"
        />

    </NuxtLayout>

    <ToTopBtn />
</template>

<script setup lang="ts">
    import { MathHelper } from "~/composables/Classes/Helper"
    import { useRequestStore } from '@/stores/requests'
    import { useMedia } from '@/stores/media'
    import { useGlobalsStore } from '@/stores/globals'
    import { CookieHandler } from "@/composables/Classes/CookieHandler"
    import { useSharedCursor } from '~/composables/useSharedCursor';

    const { locale, locales } = useI18n()
    const route = useRoute()
    const nuxtApp = useNuxtApp()
    const requestStore = useRequestStore()
    const globalsStore = useGlobalsStore()
    const mediaStore = useMedia()
    const { $iframeMessenger } = useNuxtApp()
    const { $checkAndGetToken } = useNuxtApp()

    const { pageContent, pageID, pageIndex, functionalCookies } = storeToRefs(requestStore)
    const { menuState, notificationState, globalPlayerState } = storeToRefs(globalsStore)
    const visitorName = ref<string>("USER") // Dieser Ref wird an das Composable übergeben
    const notificationContent = ref()
    const currentPagePath = computed(() => route.path)

    const cookie = new CookieHandler(requestStore)

    const scrollThreshold = 100;
    let lastScrollY = window.scrollY;
    let ticking = false;

    const pageMeta = computed(() => {
		const metaData: any = pageContent.value[pageID.value]?.pagemeta

        if (metaData) {
            useSeoMeta({
                title: 'Walbert-Schmitz GmbH' + ((metaData.title && metaData.title !== 'WS Website') ? " - " + metaData.title : ''),
                description: metaData.meta_description,
            })
        }

		return metaData
	})

    // Der Socket-Server wird direkt an das useMousePointer Composable übergeben
    const socketServer = "https://ws-realtime-comm-service.onrender.com/"; 

    mediaStore.setTracks([
        {
            title: 'Intro Episode',
            subtitle: 'Willkommen zum Podcast',
            src: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
            cover: 'https://images.unsplash.com/photo-1507874457470-272b3c8d8ee2?auto=format&fit=crop&w=600&q=80'
        },
        {
            title: 'Folge 1',
            subtitle: 'Deep Dive: Webentwicklung',
            src: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3',
            cover: 'https://images.unsplash.com/photo-1507874457470-272b3c8d8ee2?auto=format&fit=crop&w=600&q=80'
        }
    ])

    const mState = computed(() => {
        return menuState.value
    })

    // Set layout dynamiclly for each page
    const layout = computed(() => {
        let allPages = pageIndex.value?.global_index

        if(pageID.value > 0) {
            switch (allPages[pageID.value].menu) {
                case 'article':
                    return 'articles';
                default:
                    return 'default';
            }
        }
        return 'default'; // Standard-Rückgabewert, falls pageID <= 0
    })

    const siteUrl = 'https://walbert-schmitz.de'

    const normalizeCanonicalPath = (path: string) => {
        const normalizedPath = `/${path}`.replace(/\/{2,}/g, '/')

        return normalizedPath === '/'
            ? normalizedPath
            : `${normalizedPath.replace(/\/+$/, '')}/`
    }

    const canonicalHref = computed(() => `${siteUrl}${normalizeCanonicalPath(route.path)}`)

    useHead({
        htmlAttrs: {
            lang: locale.value
        },

        link: [
            {
                rel: 'canonical',
                href: canonicalHref
            },
            ...(Array.isArray(locales.value)
            ? locales.value.map(loc => ({
                rel: 'alternate',
                hreflang: loc.code,
                href: `https://walbert-schmitz.de/${loc.code}`
            }))
            : [])
        ]
    })

    const { loggedUsers } = useSharedCursor(socketServer, visitorName, functionalCookies, currentPagePath);

    // Darkmode-Logik
    if (process.client && localStorage.getItem('darkmode')) {
        const htmlRoot = document.querySelector('html') as HTMLElement;
        MathHelper.mutateEle(htmlRoot, 'add-attribute', 'darkmode')

        onMounted(() => {
            requestStore.toggleDarkmode(true)
        })
    }

    // Scroll-Handler für den Podcast-Player
    const handleScroll = () => {
        const currentY = window.scrollY
        const diff = currentY - lastScrollY

        if (!ticking) {
            window.requestAnimationFrame(() => {
                if (Math.abs(diff) > scrollThreshold) {
                    if (diff > 0) {
                        mediaStore.setVisibility(false)
                    } else {
                        mediaStore.setVisibility(true)
                    }
                    lastScrollY = currentY
                }
                ticking = false
            })
            ticking = true
        }
    }

    const checkLoginState = async () => {
        try {
            const response = await $checkAndGetToken()

            if (response) {
                requestStore.setLoginState(true)
            }
        } catch (error) {
            console.log(error)
            requestStore.setLoginState(false)
        }
    }

    // `watch` block für functionalCookies
    // Die Logik zum Verbinden/Trennen des Sockets wird jetzt im `useMousePointer` Composable gehandhabt.
    // Dieser Watcher ist nur noch für das Debugging oder andere, nicht-Maus-bezogene Reaktionen auf functionalCookies nötig.
    watch(functionalCookies, async (newVal, oldVal) => {
        // console.log('functionalCookies changed:', newVal);
    });

    const setDarkmodeFromIframe = () => {
        const htmlRoot = document.querySelector('html') as HTMLElement

        MathHelper.mutateEle(htmlRoot, 'toggle-attribute', 'darkmode')

        if (htmlRoot.hasAttribute('darkmode')) {
            localStorage.setItem('darkmode', 'true')
            requestStore.toggleDarkmode(true)
        } else {
            localStorage.removeItem('darkmode')
            requestStore.toggleDarkmode(false)
        }
    }

    onMounted(() => {
        if (localStorage.getItem('visitorName')) {
            visitorName.value = localStorage.getItem('visitorName') as string
        }

        $iframeMessenger.on('darkmode', setDarkmodeFromIframe)
        // Überprüfe und setze funktionale Cookies beim Mounten
        if (cookie.getCookie("functionalCookies")) {
            requestStore.setFunctionalCookies(true)
        }

        $iframeMessenger.on('name', (payload: string) => {
            localStorage.setItem('visitorName', payload)
            visitorName.value = payload
        })

        // Füge den Scroll-Event-Listener nur auf der Client-Seite hinzu
        if (process.client) {
            window.addEventListener('scroll', handleScroll)
        }

        // REALTIME SYNC SETUP BEGIN
        nuxtApp.$realtimeSync.on('article_saved', (data) => {
            console.log('Artikel gespeichert:', data.payload.page.title)
            globalsStore.changeNotificationState(true)
            notificationContent.value = data.payload.page
        })

        // Beispiel für pingServer, falls noch benötigt
        const pingServer = () => {
            nuxtApp.$realtimeSync.send({
                event: 'client_ping',
                time: new Date().toISOString()
            })
        }
        // REALTIME SYNC SETUP END

        checkLoginState()
    })

    onBeforeUnmount(() => {
        // Entferne den Scroll-Event-Listener nur auf der Client-Seite
        if (process.client) {
            window.removeEventListener('scroll', handleScroll)
        }
    })
</script>
